// src/modules/app-views/invoices/pages/Document/modals/PaymentModal.jsx
// *******************************************************************************
// PAYMENT MODAL
// *******************************************************************************
// Modal for adding payment methods to invoices.
// Uses SEFAZ payment types (tPag) for fiscal compliance.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo, useCallback } from 'react';
import { Row, Col, Alert, Typography, Divider } from 'antd';
import { CreditCardOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleCurrency from 'components/SimpleComponents/SimpleCurrency';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';
import fiscalService from 'services/fiscalService';

const { Text, Title } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

/**
 * Build payment type options for SimpleSelect
 * Transforms SEFAZ payment types to select options with translated labels
 */
function buildPaymentTypeOptions() {
  return Object.entries(fiscalService.PAYMENT_TYPES).map(([key, value]) => ({
    value: value.code,
    label: translateX(value.labelKey),
    labelKey: value.labelKey,
  }));
}

// *******************************************************************************
// STEP COMPONENT
// *******************************************************************************

/**
 * Payment form content
 */
const PaymentForm = ({ invoiceTotal, paidAmount }) => {
  const remainingBalance = financialService.calculateRemainingBalance(invoiceTotal, paidAmount);
  const paymentOptions = useMemo(() => buildPaymentTypeOptions(), []);

  return (
    <div>
      {/* Balance Info */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '12px 16px',
        background: remainingBalance > 0 ? '#fff7e6' : '#f6ffed',
        borderRadius: 6,
        border: remainingBalance > 0 ? '1px solid #ffd591' : '1px solid #b7eb8f',
        marginBottom: 16,
      }}>
        <div>
          <Text type="secondary">{translateX('invoice_total')}:</Text>
          <Title level={4} style={{ margin: 0 }}>
            {financialService.formatCurrency(invoiceTotal)}
          </Title>
        </div>
        <div>
          <Text type="secondary">{translateX('already_paid')}:</Text>
          <Title level={4} style={{ margin: 0, color: '#52c41a' }}>
            {financialService.formatCurrency(paidAmount)}
          </Title>
        </div>
        <div>
          <Text type="secondary">{translateX('remaining')}:</Text>
          <Title level={4} style={{ margin: 0, color: remainingBalance > 0 ? '#faad14' : '#52c41a' }}>
            {financialService.formatCurrency(remainingBalance)}
          </Title>
        </div>
      </div>

      {remainingBalance <= 0 && (
        <Alert
          title={translateX('invoice_fully_paid')}
          description={translateX('invoice_fully_paid_description')}
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Divider style={{ margin: '12px 0' }} />

      <Row gutter={[16, 16]}>
        <Col xs={24} md={16}>
          <SimpleSelect
            name="code"
            label="payment_method"
            options={paymentOptions}
            required
            showSearch
            placeholder="select_payment_method"
          />
        </Col>

        <Col xs={24} md={8}>
          <SimpleCurrency
            name="amount"
            label="amount"
            min={0.01}
            required
            currencies={[{ value: 'BRL', label: 'R$' }]}
          />
        </Col>

        <Col xs={24}>
          <SimpleInput
            name="description"
            label="description_optional"
            placeholder="payment_description_placeholder"
          />
        </Col>
      </Row>

      {/* Hint for change */}
      <Alert
        title={translateX('payment_hint')}
        description={translateX('payment_hint_description')}
        type="info"
        showIcon
        style={{ marginTop: 16 }}
      />
    </div>
  );
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const PaymentModal = ({
  show,
  setShow,
  onConfirm,
  invoiceTotal = 0,
  paidAmount = 0,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const remainingBalance = financialService.calculateRemainingBalance(invoiceTotal, paidAmount);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = useCallback(async ({ form }) => {
    const values = form.getFieldsValue(true);

    // Get payment type info
    const paymentType = fiscalService.getPaymentTypeByCode(values.code);

    // Build payment data
    const paymentData = {
      code: values.code,
      type: paymentType?.type || 'OTHER',
      amount: financialService.roundCurrency(values.amount || 0),
      description: values.description || '',
    };

    onConfirm?.(paymentData);
    return true;
  }, [onConfirm]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const initialValues = useMemo(() => ({
    code: '01', // Default to money
    amount: remainingBalance > 0 ? remainingBalance : 0,
    description: '',
  }), [remainingBalance]);

  const steps = useMemo(() => [
    {
      stepTitle: 'payment',
      validationFields: ['code', 'amount'],
      content: () => (
        <PaymentForm
          invoiceTotal={invoiceTotal}
          paidAmount={paidAmount}
        />
      ),
    },
  ], [invoiceTotal, paidAmount]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      title="add_payment"
      icon={<CreditCardOutlined />}
      steps={steps}
      onFinish={handleFinish}
      initialValues={initialValues}
      width={560}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PaymentModal;
